import React, { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

// Admins manage services under Organisation Setup, so they get no Services tab here
function useIsAdmin() {
  const { myRole } = useWorkspace();
  return myRole === 'super_admin' || myRole === 'admin';
}

export function ContractsIndex() {
  return <Navigate to={useIsAdmin() ? 'agreements' : 'services'} replace />;
}

export default function ContractsShell() {
  const { appId } = useParams();
  const api = useApi();
  const isAdmin = useIsAdmin();

  const [counts, setCounts] = useState({
    clients: null, services: null, agreements: null,
    tasks: null, pendingReview: null,
  });

  useEffect(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const year  = now.getFullYear();

    Promise.allSettled([
      api.listContractClients(appId, {}),
      api.listContractServices(appId, {}),
      api.listContractAgreements(appId, { status: 'active' }),
      api.listContractTasks(appId, {}),
      api.getComplianceBoard(appId, { month, year }),
    ]).then(([clients, services, agreements, tasks, board]) => {
      const clientCount    = clients.status    === 'fulfilled' ? (clients.value?.length    ?? null) : null;
      const serviceCount   = services.status   === 'fulfilled' ? (services.value?.length   ?? null) : null;
      const agreementCount = agreements.status === 'fulfilled' ? (agreements.value?.length ?? null) : null;
      const taskCount      = tasks.status === 'fulfilled' && Array.isArray(tasks.value) ? tasks.value.length : null;

      let pendingReview = null;
      if (board.status === 'fulfilled' && Array.isArray(board.value)) {
        const allSvcs = board.value.flatMap(ag => [
          ...(ag.periodic?.services || []),
          ...(ag.one_time?.services  || []),
        ]);
        pendingReview = allSvcs.filter(s => s.task?.check_status === 'pending_review').length;
      }

      setCounts({
        clients:       clientCount,
        services:      serviceCount,
        agreements:    agreementCount,
        tasks:         taskCount,
        pendingReview,
      });
    });
  }, [appId]);

  const TABS = [
    ...(isAdmin ? [] : [{ to: 'services', label: 'Services' }]),
    { to: 'agreements',       label: 'Agreements'       },
    { to: 'compliance-board', label: 'Compliance Board' },
  ];

  return (
    <div className="setup-page">
      <h1 className="page-title">Agreements</h1>
      <p className="page-subtitle">
        Manage client service agreements, recurring compliance tasks, and
        maker–checker verification workflows.
      </p>

      <div className="setup-tabs" role="tablist">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={`/apps/${appId}/contracts/${t.to}`}
            className={({ isActive }) => `setup-tab ${isActive ? 'active' : ''}`}
            role="tab"
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span style={{
                marginLeft: 6,
                background: t.countColor || 'var(--brand-light)',
                color: t.countColor || 'var(--brand)',
                borderRadius: 10,
                padding: '1px 7px',
                fontSize: 11,
                fontWeight: 700,
                verticalAlign: 'middle',
              }}>{t.count}</span>
            )}
          </NavLink>
        ))}
      </div>

      <div className="setup-tab-body">
        <Outlet />
      </div>
    </div>
  );
}
